import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  ADMINISTRATOR_SCOPES,
  normalizeAdministratorScope,
  getHighestAdministratorScope,
  getAllowedAdministratorScopes,
  canAccessAdministrators,
} from '../utils/administratorScopes.js';

let logoutTimer;

const AuthContext = React.createContext({
  token: '',
  userId: '',
  userName: '',
  email: '',
  scope: '',
  accountId: '',
  isLoggedIn: false,
  login: (token, userId, userName, email, scope, tokenExpiration) => {},
  logout: () => {},
  scopeCheck: (requiredScope) => false,
  normalizedScope: '',
  isSuperAdmin: false,
  isAdmin: false,
  isClientAdmin: false,
  isClientViewer: false,
  isClient: false,
  canAccessOverview: false,
  canAccessAdministrators: false,
  allowedAdministratorScopes: []
});

const calculateRemainingTime = (expirationTime) => {
  const currentTime               = new Date().getTime();
  const adjExpirationTime         = new Date(expirationTime).getTime();

  const remainingDuration         = adjExpirationTime - currentTime;

  return remainingDuration;
}

const retrieveStoredToken = () => {
  const storedToken               = localStorage.getItem('token');
  const storedTokenExpiration     = localStorage.getItem('tokenExpiration');

  const remainingTime             = calculateRemainingTime(storedTokenExpiration);

  if(remainingTime <= 1000) {

    localStorage.removeItem('token');
    localStorage.removeItem('tokenExpiration');
    localStorage.removeItem('userId');
    localStorage.removeItem('userName');
    localStorage.removeItem('email');
    localStorage.removeItem('scope');
    localStorage.removeItem('accountId');

    return null;
  }
  
  return {
    token: storedToken,
    duration: remainingTime
  };
}

export const AuthContextProvider = (props) => {

  const tokenData                 = retrieveStoredToken();
  let initialToken;
  if(tokenData) {
    initialToken                  = tokenData.token;
  }
  const initialUserId             = localStorage.getItem('userId');
  const initialUserName           = localStorage.getItem('userName');
  const initialEmail              = localStorage.getItem('email');
  const initialScope              = normalizeAdministratorScope(localStorage.getItem('scope'));
  const initialAccountId          = localStorage.getItem('accountId');

  const [token, setToken]         = useState(initialToken);
  const [userId, setUserId]       = useState(initialUserId);
  const [userName, setUserName]   = useState(initialUserName);
  const [email, setEmail]         = useState(initialEmail);
  const [scope, setScope]         = useState(initialScope);
  const [accountId, setAccountId] = useState(initialAccountId);
  
  const userIsLoggedIn            = !!token;

  const decodedTokenScopes = useMemo(() => {
    if (!token) {
      return [];
    }
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      const scopeValues = Array.isArray(payload.scope)
        ? payload.scope
        : payload.scope
          ? [payload.scope]
          : [];
      return scopeValues.map(normalizeAdministratorScope).filter(Boolean);
    } catch (error) {
      console.error("Error decoding token scope:", error);
      return [];
    }
  }, [token]);

  const normalizedScope = useMemo(() => {
    return getHighestAdministratorScope([scope, decodedTokenScopes]);
  }, [scope, decodedTokenScopes]);

  // Scope check functions - decode JWT token and check scope
  const scopeCheck = useCallback((requiredScope) => {
    const normalizedRequiredScope = normalizeAdministratorScope(requiredScope);
    if (!normalizedRequiredScope) return false;
    return decodedTokenScopes.includes(normalizedRequiredScope) || normalizedScope === normalizedRequiredScope;
  }, [decodedTokenScopes, normalizedScope]);

  const isSuperAdmin = normalizedScope === ADMINISTRATOR_SCOPES.SUPER;
  const isAdmin = normalizedScope === ADMINISTRATOR_SCOPES.ADMIN;
  const isClientAdmin = normalizedScope === ADMINISTRATOR_SCOPES.CLIENT_ADMIN;
  const isClientViewer = normalizedScope === ADMINISTRATOR_SCOPES.CLIENT_VIEWER;
  const isClient = isClientViewer;
  const hasOverviewAccess = String(accountId) === "0" && !isClientAdmin && !isClientViewer;
  const hasAdministratorsAccess = canAccessAdministrators(normalizedScope);
  const allowedAdministratorScopes = getAllowedAdministratorScopes(normalizedScope);

  const logoutHandler = useCallback(() => {
    
    setToken(null);
    setUserId(null);
    setUserName(null);
    setEmail(null);
    setScope(null);
    setAccountId(null);

    localStorage.removeItem('token');
    localStorage.removeItem('tokenExpiration');
    localStorage.removeItem('userId');
    localStorage.removeItem('userName');
    localStorage.removeItem('email');
    localStorage.removeItem('scope');
    localStorage.removeItem('accountId');

    if(logoutTimer) {
      clearTimeout(logoutTimer);
    }
  }, []);

  useEffect(() => {
    if(tokenData) {
      logoutTimer                 = setTimeout(logoutHandler, tokenData.duration);
    }
  }, [tokenData, logoutHandler]);

  const loginHandler = (token, userId, userName, email, scope, accountId, tokenExpiration) => {
    const normalizedLoginScope = normalizeAdministratorScope(scope);
    
    setAccountId(accountId);
    setUserId(userId);
    setUserName(userName);
    setEmail(email);
    setScope(normalizedLoginScope);
    setToken(token);

    localStorage.setItem('token', token);
    localStorage.setItem('tokenExpiration', tokenExpiration);
    localStorage.setItem('userId', userId.toString());
    localStorage.setItem('userName', userName);
    localStorage.setItem('email', email);
    localStorage.setItem('scope', normalizedLoginScope);
    localStorage.setItem('accountId', accountId.toString());

    const remainingTime           = calculateRemainingTime(tokenExpiration);

    logoutTimer                   = setTimeout(logoutHandler, remainingTime);

  }

  const contextValue = {
    token: token,
    userId: userId,
    userName: userName,
    email: email,
    scope: normalizedScope,
    normalizedScope: normalizedScope,
    accountId: accountId,
    isLoggedIn: userIsLoggedIn,
    login: loginHandler,
    logout: logoutHandler,
    scopeCheck: scopeCheck,
    isSuperAdmin: isSuperAdmin,
    isAdmin: isAdmin,
    isClientAdmin: isClientAdmin,
    isClientViewer: isClientViewer,
    canAccessOverview: hasOverviewAccess,
    canAccessAdministrators: hasAdministratorsAccess,
    allowedAdministratorScopes: allowedAdministratorScopes,
    isClient: isClient
  }
  console.log(contextValue);
  return <AuthContext.Provider value={contextValue}>{props.children}</AuthContext.Provider>;
}

export default AuthContext;
